import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { UPDATE_NOTIFICATION_PREFERENCES, SEND_PHONE_VERIFICATION, VERIFY_PHONE_NUMBER } from '../graphql/mutations';
import {
  AccountCircle as AccountIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  CheckCircle as CheckIcon,
  ConstructionRounded as ComingSoonIcon,
  ContactSupport as SupportIcon,
  Event as EventIcon,
  Logout as LogoutIcon,
  MarkEmailRead as EmailIcon,
  NotificationsNone as NotificationsIcon,
  Person as PersonIcon,
  Smartphone as AppIcon,
  Star as StarIcon,
  Storage as DataIcon,
  AdminPanelSettings as AdminPanelIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { computePermissions } from '../hooks/useUserRole';
import { User } from '../types';
import { UserConfigurationsSection } from './UserConfigurationsSection';
import { TenantConfigurationsSection } from './TenantConfigurationsSection';
import { BillingSection } from './BillingSection';

// ─── ConfigurationsSection ────────────────────────────────────────────────────
// Tabbed wrapper: "User Configurations" for all paid/admin users,
// plus "Tenant Configurations" tab for admins only (roleId ≤ 2).

const ConfigurationsSection: React.FC<{ user: User }> = ({ user }) => {
  const { isClientAdmin: isAdmin } = computePermissions(user);
  const [tab, setTab] = useState(0);

  return (
    <Card elevation={2} sx={{ borderRadius: 3, mt: 3 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="configurations tabs">
          <Tab label="User Configurations" />
          {isAdmin && <Tab label="Tenant Configurations" />}
        </Tabs>
      </Box>
      <Box hidden={tab !== 0}>
        <UserConfigurationsSection user={user} />
      </Box>
      {isAdmin && (
        <Box hidden={tab !== 1}>
          <TenantConfigurationsSection user={user} />
        </Box>
      )}
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface UserPortalProps {
  user: User;
  onSwitchToAdmin?: () => void;
}

// Derive the plan label from the user's roleId.
// roleId 1–2: admin (should not reach here), 3–98: paid plans, 99+: free tier.
// Map is deliberately loose – the api/backend owns the ground truth.
const getPlanLabel = (roleId: number): { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' } => {
  if (roleId <= 2) return { label: 'Admin', color: 'secondary' };
  if (roleId <= 98) return { label: 'Personal', color: 'primary' };
  return { label: 'Free', color: 'default' };
};

const getInitials = (user: User): string => {
  const first = user.firstName?.charAt(0) ?? '';
  const last = user.lastName?.charAt(0) ?? '';
  return (first + last).toUpperCase() || user.username?.charAt(0).toUpperCase() || '?';
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
};

export const UserPortal: React.FC<UserPortalProps> = ({ user, onSwitchToAdmin }) => {
  const { logout } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const plan = getPlanLabel(user.roleId ?? 999);
  const initials = getInitials(user);

  /** Format up to 10 digits into (XXX) XXX-XXXX */
  const formatLocalPhone = (raw: string): string => {
    const d = raw.replace(/\D/g, '').slice(0, 10);
    if (d.length === 0) return '';
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  // Notification preferences state — seeded from the current user data
  const prefs = user.notificationPreferences;
  const savedPhone = prefs?.phoneNumber ?? '';
  const [notifEnabled, setNotifEnabled] = useState(prefs?.enabled ?? false);
  const [smsEnabled, setSmsEnabled] = useState(prefs?.smsEnabled ?? false);
  // Phone is stored as E.164 (+16175550100); we split into country code + local digits for display/entry.
  // Only +1 (US) is supported for now — the Select is shown but locked.
  const countryCode = '+1';
  const [localNumber, setLocalNumber] = useState(
    formatLocalPhone(savedPhone.startsWith('+1') ? savedPhone.slice(2) : savedPhone)
  );
  const [signalREnabled, setSignalREnabled] = useState(prefs?.signalREnabled ?? false);
  const [notifSaved, setNotifSaved] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);

  // Phone verification state machine
  type VerifyStep = 'idle' | 'sending' | 'awaitingCode' | 'verifying' | 'verified';
  const [verifyStep, setVerifyStep] = useState<VerifyStep>('idle');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  // Track which phone was successfully verified this session (pre-seed with saved phone)
  const [verifiedPhone, setVerifiedPhone] = useState(savedPhone);

  // Build the full E.164 number from the two parts
  const normalizedPhone = localNumber.trim() ? countryCode + localNumber.replace(/\D/g, '') : '';

  // Sync whenever the user's notification preferences arrive or change (Apollo cache-and-network)
  useEffect(() => {
    setNotifEnabled(user.notificationPreferences?.enabled ?? false);
    setSmsEnabled(user.notificationPreferences?.smsEnabled ?? false);
    const raw = user.notificationPreferences?.phoneNumber ?? '';
    setLocalNumber(formatLocalPhone(raw.startsWith('+1') ? raw.slice(2) : raw));
    setSignalREnabled(user.notificationPreferences?.signalREnabled ?? false);
    // Reset verification — saved phone is already trusted
    setVerifiedPhone(user.notificationPreferences?.phoneNumber ?? '');
    setVerifyStep('idle');
    setVerifyCode('');
    setVerifyError(null);
  }, [
    user.notificationPreferences?.enabled,
    user.notificationPreferences?.smsEnabled,
    user.notificationPreferences?.phoneNumber,
    user.notificationPreferences?.signalREnabled,
  ]);

  const [updateNotificationPreferences, { loading: savingNotif }] = useMutation(
    UPDATE_NOTIFICATION_PREFERENCES,
    {
      onCompleted: () => setNotifSaved(true),
      onError: (err) => setNotifError(err.message || 'Failed to save preferences'),
    }
  );

  const [sendPhoneVerification] = useMutation(SEND_PHONE_VERIFICATION);
  const [verifyPhoneNumberMutation] = useMutation(VERIFY_PHONE_NUMBER);

  // Whether the phone field has been changed from the saved value
  const phoneIsChanged = normalizedPhone !== (user.notificationPreferences?.phoneNumber ?? '');
  // Verification is required when SMS is on and the phone number has been changed
  const requiresVerification = smsEnabled && !!localNumber.trim() && phoneIsChanged;
  const canSave = !savingNotif
    && verifyStep !== 'sending'
    && verifyStep !== 'verifying'
    && verifyStep !== 'awaitingCode';

  const handleSendVerification = async () => {
    const phone = normalizedPhone;
    setVerifyStep('sending');
    setVerifyError(null);
    try {
      const { data, errors } = await sendPhoneVerification({
        variables: { userId: user.id, phoneNumber: phone },
      });
      if (errors?.length || !data?.sendPhoneVerification) {
        setVerifyError(errors?.[0]?.message || 'Failed to send verification code.');
        setVerifyStep('idle');
      } else {
        setVerifyStep('awaitingCode');
      }
    } catch (err: any) {
      setVerifyError(err.message || 'Failed to send verification code.');
      setVerifyStep('idle');
    }
  };

  const handleVerifyCode = async () => {
    const phone = normalizedPhone;
    setVerifyStep('verifying');
    setVerifyError(null);
    try {
      const { data, errors } = await verifyPhoneNumberMutation({
        variables: { userId: user.id, phoneNumber: phone, code: verifyCode },
      });
      if (errors?.length) {
        setVerifyError(errors[0].message || 'Verification failed.');
        setVerifyStep('awaitingCode');
      } else if (data?.verifyPhoneNumber) {
        setVerifiedPhone(phone);
        setVerifyStep('verified');
        setVerifyCode('');
      } else {
        setVerifyError('Incorrect code. Please try again.');
        setVerifyStep('awaitingCode');
      }
    } catch (err: any) {
      setVerifyError(err.message || 'Verification failed.');
      setVerifyStep('awaitingCode');
    }
  };

  const handleCancelVerification = () => {
    setVerifyStep('idle');
    setVerifyCode('');
    setVerifyError(null);
  };

  // Called from Save button — sends code if verification needed, otherwise saves directly
  const handleSaveOrVerify = () => {
    if (requiresVerification && verifiedPhone !== normalizedPhone) {
      handleSendVerification();
    } else {
      handleSaveNotifications();
    }
  };

  const handleSaveNotifications = () => {
    setNotifError(null);
    updateNotificationPreferences({
      variables: {
        userId: user.id,
        preferences: {
          enabled: notifEnabled,
          smsEnabled,
          phoneNumber: smsEnabled ? normalizedPhone : undefined,
          signalREnabled,
        },
      },
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);
  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Unified header — nav strip + identity hero in one gradient block */}
      <Box
        sx={{
          background: 'linear-gradient(150deg, #0d47a1 0%, #1565c0 45%, #0288d1 100%)',
          color: 'white',
          pb: 4,
        }}
      >
        {/* Top nav row */}
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Logo + wordmark */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box
              component="img"
              src="/assets/images/TalkForTy-icon.png"
              alt="TalkForTy"
              sx={{ height: 34, mr: 1.25, borderRadius: 1, opacity: 0.95 }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, letterSpacing: 0.3, fontSize: '1.05rem', opacity: 0.95 }}
            >
              TalkForTy
            </Typography>
          </Box>

          {/* Control Panel shortcut for admins */}
          {onSwitchToAdmin && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AdminPanelIcon />}
              onClick={onSwitchToAdmin}
              sx={{
                mr: 2,
                color: 'white',
                borderColor: 'rgba(255,255,255,0.45)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 2,
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.12)' },
              }}
            >
              Control Panel
            </Button>
          )}

          {/* Avatar / account menu */}
          <Tooltip title="Account options">
            <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0.5 }}>
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  background: 'rgba(255,255,255,0.18)',
                  border: '2px solid rgba(255,255,255,0.45)',
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'white',
                  backdropFilter: 'blur(4px)',
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ elevation: 3, sx: { mt: 1, minWidth: 200, borderRadius: 2 } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {user.displayName || user.firstName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user.emailAddress}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ py: 1.25 }}>
              <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: 'error.main' }} />
              <Typography variant="body2" color="error.main" fontWeight={500}>Sign out</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>

        {/* Identity hero */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-end' },
            gap: { xs: 2, sm: 3 },
            px: { xs: 3, sm: 4 },
            pt: 1,
          }}
        >
          {/* Large avatar */}
          <Avatar
            sx={{
              width: 80,
              height: 80,
              background: 'rgba(255,255,255,0.18)',
              border: '3px solid rgba(255,255,255,0.5)',
              fontSize: 30,
              fontWeight: 700,
              color: 'white',
              flexShrink: 0,
              backdropFilter: 'blur(6px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            {initials}
          </Avatar>

          {/* Name + subtitle */}
          <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, pb: { sm: 0.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {user.firstName || user.displayName || user.username}
              </Typography>
              <Chip
                label={plan.label}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.22)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  border: '1px solid rgba(255,255,255,0.4)',
                  height: 22,
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.5 }}>
              {user.emailAddress}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* ── Left column ── */}
          <Grid item xs={12} md={7}>
            {/* Profile card */}
            <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <PersonIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Profile
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={2}>
                  {[
                    { label: 'Display name', value: user.displayName },
                    { label: 'First name', value: user.firstName },
                    { label: 'Last name', value: user.lastName },
                    { label: 'Username', value: user.username },
                    { label: 'Email address', value: user.emailAddress },
                    { label: 'Language', value: user.languageCode?.toUpperCase() || '—' },
                  ].map(({ label, value }) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {label}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {value || '—'}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Account status card */}
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <DataIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Account
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Plan
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <StarIcon sx={{ fontSize: 16, color: plan.color === 'primary' ? '#1565c0' : 'text.disabled' }} />
                      <Chip label={plan.label} color={plan.color} size="small" />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <CheckIcon sx={{ fontSize: 16, color: user.isActive ? 'success.main' : 'error.main' }} />
                      <Typography variant="body2" fontWeight={500}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Member since
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <EventIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(user.createdDate)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last login
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <AccountIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(user.lastLoginDate)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Identity provider
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {user.identityProvider || '—'}
                    </Typography>
                  </Grid>
                </Grid>

                {user.mappings && user.mappings.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Organisations
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {user.mappings.map((m) => (
                        <Chip
                          key={m.tenantId}
                          label={`Org: ${m.tenantId}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Configurations — tabbed card for paid/admin users */}
            {computePermissions(user).isPaidUser && <ConfigurationsSection user={user} />}

            {/* Notification preferences card */}
            <Card elevation={2} sx={{ borderRadius: 3, mt: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <NotificationsIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Notifications
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {notifError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setNotifError(null)}>
                    {notifError}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifEnabled}
                        onChange={(e) => setNotifEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Push notifications</Typography>
                        <Typography variant="caption" color="text.secondary">Receive alerts for important account activity</Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', py: 0.75 }}
                  />

                  <Divider />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={signalREnabled}
                        onChange={(e) => setSignalREnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>In-app notifications</Typography>
                        <Typography variant="caption" color="text.secondary">Show real-time notifications while using the app</Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', py: 0.75 }}
                  />

                  <Divider />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={smsEnabled}
                        onChange={(e) => setSmsEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>SMS notifications</Typography>
                        <Typography variant="caption" color="text.secondary">Receive text messages for critical alerts</Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', py: 0.75 }}
                  />

                  {smsEnabled && (
                    <Box sx={{ mt: 1.5 }}>
                      {/* Country code + local number — shown identically when viewing or editing */}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                          <InputLabel>Code</InputLabel>
                          <Select
                            value="+1"
                            label="Code"
                            disabled
                            sx={{ bgcolor: 'action.hover' }}
                          >
                            <MenuItem value="+1">🇺🇸 +1</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          label="Phone number"
                          value={localNumber}
                          onChange={(e) => {
                            setLocalNumber(formatLocalPhone(e.target.value));
                            if (verifyStep !== 'idle') handleCancelVerification();
                          }}
                          disabled={verifyStep === 'awaitingCode' || verifyStep === 'verifying'}
                          size="small"
                          fullWidth
                          placeholder="(617) 555-0100"
                          inputProps={{ maxLength: 14 }}
                          InputProps={{
                            endAdornment: verifyStep === 'verified' ? (
                              <InputAdornment position="end">
                                <Chip
                                  label="Verified"
                                  color="success"
                                  size="small"
                                  icon={<CheckIcon sx={{ fontSize: '14px !important' }} />}
                                />
                              </InputAdornment>
                            ) : undefined,
                          }}
                        />
                      </Box>

                      {/* Code entry — shown after Save sends the OTP */}
                      {(verifyStep === 'awaitingCode' || verifyStep === 'verifying') && (
                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            We texted a 6-digit code to {normalizedPhone}. Enter it below.
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 0.5 }}>
                            <TextField
                              autoFocus
                              label="Verification code"
                              value={verifyCode}
                              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              size="small"
                              placeholder="000000"
                              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                              sx={{ width: 150 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleVerifyCode}
                              disabled={verifyCode.length !== 6 || verifyStep === 'verifying'}
                              sx={{ mt: 0.25, textTransform: 'none', minWidth: 90 }}
                            >
                              {verifyStep === 'verifying'
                                ? <CircularProgress size={16} color="inherit" />
                                : 'Confirm'}
                            </Button>
                            <Button
                              variant="text"
                              size="small"
                              onClick={handleCancelVerification}
                              sx={{ mt: 0.25, textTransform: 'none', color: 'text.secondary' }}
                            >
                              Cancel
                            </Button>
                          </Box>
                          {verifyError && (
                            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setVerifyError(null)}>
                              {verifyError}
                            </Alert>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveOrVerify}
                    disabled={!canSave}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, minWidth: 140 }}
                  >
                    {verifyStep === 'sending'
                      ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Sending code…</>
                      : requiresVerification && verifiedPhone !== normalizedPhone
                        ? 'Save & Verify'
                        : savingNotif ? 'Saving…' : 'Save preferences'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ── Right column ── */}
          <Grid item xs={12} md={5}>
            {/* Subscription / Billing card */}
            <Box sx={{ mb: 3 }}>
              <BillingSection user={user} />
            </Box>

            {/* Get the App card */}
            <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AppIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>
                      Get the App
                    </Typography>
                  </Box>
                  <Chip
                    icon={<ComingSoonIcon sx={{ fontSize: '14px !important' }} />}
                    label="Coming soon"
                    size="small"
                    sx={{
                      backgroundColor: '#fff8e1',
                      color: '#f57f17',
                      border: '1px solid #ffe082',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                <Divider sx={{ mb: 2.5 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  TalkForTy is coming to iOS and Android. Sign up to be notified when it's available.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AppleIcon />}
                    disabled
                    sx={{ justifyContent: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Download on the App Store
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<AndroidIcon />}
                    disabled
                    sx={{ justifyContent: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Get it on Google Play
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Help & Support card */}
            <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <SupportIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Help &amp; Support
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2.5 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Need help with your account, the app, or your subscription?
                </Typography>

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<EmailIcon />}
                  href="mailto:support@dommicro.com"
                  sx={{ justifyContent: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 600, mb: 1.5 }}
                >
                  Email support
                </Button>

                {plan.label === 'Free' && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: '#e3f2fd',
                      borderRadius: 2,
                      border: '1px solid #90caf9',
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} color="primary.dark" display="block" gutterBottom>
                      Upgrade your plan
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Unlock AI-powered suggestions, custom boards, and more with a Personal subscription from just $12.99/month (annual).
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Sign out card */}
            <Card elevation={0} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Not finished yet? You can safely sign out and your settings will be preserved.
                </Typography>
                <Button
                  variant="text"
                  color="error"
                  startIcon={<LogoutIcon />}
                  onClick={logout}
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                >
                  Sign out
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Success snackbar */}
      <Snackbar
        open={notifSaved}
        autoHideDuration={4000}
        onClose={() => setNotifSaved(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setNotifSaved(false)} sx={{ width: '100%' }}>
          Notification preferences saved.
        </Alert>
      </Snackbar>
    </Box>
  );
};
